import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
// Local Clip interface (moved from shared package)
interface Clip {
    id: string;
    title: string;
    description: string;
    thumbnailUrl?: string;
    videoUrl: string;
    clipUrl?: string;
    subtitleUrl?: string;
    jobId: string;
    vodId: string;
    startTime: number;
    endTime: number;
    duration: number;
    createdAt: string;
    updatedAt: string;
}
import { Crown, ArrowRight } from 'lucide-react';

// Local extended Clip interface for compatibility
interface ExtendedClip extends Clip {
    filePath?: string;
    fileName?: string;
    reason?: string;
    explanation?: string;
}
import { useAuth, User } from '../context/AuthContext'; // Import User type
import { useSubscription } from '../context/SubscriptionContext';
import { useJobProgress } from '../context/JobProgressContext';
import { api } from '../api'; // Import shared API instance
import VodUploadSection from '../components/VodUploadSection';
import JobQueueList from '../components/JobQueueList';
import ModernVodUploadForm from '../components/ModernVodUploadForm';
import ModernJobItem from '../components/ModernJobItem';

// CircularProgress component (SubscriptionPage.tsx dosyasından kopyalandı)
interface CircularProgressProps {
  percentage: number;
  color: string;
  size?: number;
  strokeWidth?: number;
  displayText?: string;
}

const CircularProgress: React.FC<CircularProgressProps> = ({
  percentage,
  color,
  size = 50, // SubscriptionPage'den biraz daha küçük
  strokeWidth = 5,
  displayText,
}) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const normalizedPercentage = Math.min(100, Math.max(0, percentage));
  const offset = circumference - (normalizedPercentage / 100) * circumference;

  return (
    <div className="relative flex flex-col items-center justify-center" style={{ width: size, height: size }}>
      <svg className="transform -rotate-90" width={size} height={size}>
        <circle
          className="text-gray-300 dark:text-gray-700" // Adjusted dark mode background
          strokeWidth={strokeWidth}
          stroke="currentColor"
          fill="transparent"
          r={radius}
          cx={size / 2}
          cy={size / 2}
        />
        <circle
          className={color} // Progress circle color
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          stroke="currentColor"
          fill="transparent"
          r={radius}
          cx={size / 2}
          cy={size / 2}
          style={{ transition: 'stroke-dashoffset 0.3s ease-out' }}
        />
      </svg>
      {displayText && (
         <span className="absolute text-xs font-semibold text-foreground text-center">
          {displayText}
        </span>
      )}
    </div>
  );
};


// İş veri yapısı için temel tip (Backend'den gelecek) - Keeping this interface
interface Job {
    id: string;
    vodUrl: string;
    vodTitle?: string;
    platform?: string;
    status: string;
    chatHistory?: any;
    llmInputTranscript?: string;
    // Update llmOutputHighlights to match JobItem's Job interface
    llmOutputHighlights?: { start: number; end: number; reason?: string; startTime?: number; endTime?: number; explanation?: string }[];
    createdAt: string;
    updatedAt: string;
    clips: ExtendedClip[];
    whisperModel?: string;
    whisperDevice?: string;
    llmCost?: number; // Added to match JobItem
    fullVodSubtitlePath?: string; // Added to match JobItem
}

// Helper function to determine alert styles
const getAlertStyles = (type: 'info' | 'warning' | 'error') => {
    switch (type) {
        case 'info':
            // Using primary for info, consistent with Geist's typical usage for informational emphasis
            return 'bg-primary-lighter border border-primary-light text-primary-dark'; // Adjusted for Geist palette
        case 'warning':
            return 'bg-warning-lighter border border-warning-light text-warning-dark'; // Geist warning colors
        case 'error':
            return 'bg-error-lighter border border-error-light text-error-dark'; // Geist error colors
        default:
            return 'bg-secondary-lighter border border-border text-foreground'; // Geist secondary/accent colors
    }
};

// Custom Button Component with Gradient and Glow
const GradientButton = ({ children, onClick, className = '', ...props }: React.ButtonHTMLAttributes<HTMLButtonElement>) => (
    <button
        onClick={onClick}
        className={`bg-gradient-to-r from-primary to-secondary text-primary-foreground font-semibold py-2 px-4 rounded-md shadow-md transition duration-300 ease-in-out transform hover:scale-105 hover:shadow-lg hover:shadow-primary/50 ${className}`}
        {...props}
      >
        {children}
    </button>
);

// Custom Link Button Component
const GradientLinkButton = ({ children, to, className = '', ...props }: { children: React.ReactNode, to: string, className?: string }) => (
    <Link
        to={to}
        className={`inline-block bg-gradient-to-r from-primary to-secondary text-primary-foreground font-semibold py-2 px-4 rounded-md shadow-md transition duration-300 ease-in-out transform hover:scale-105 hover:shadow-lg hover:shadow-primary/50 text-center ${className}`}
        {...props}
      >
        {children}
    </Link>
);

// Custom Outlined Link Button Component
const OutlinedLinkButton = ({ children, to, className = '', ...props }: { children: React.ReactNode, to: string, className?: string }) => (
     <Link
        to={to}
        className={`inline-block border border-primary text-primary hover:bg-primary hover:text-primary-foreground font-semibold py-1 px-3 rounded-md text-sm transition duration-300 ease-in-out text-center ${className}`}
        {...props}
      >
        {children}
    </Link>
);


function Dashboard() {
    const { user: contextUser, isLoading: authLoading, isAuthenticated, registerOnLoginSuccess, unregisterOnLoginSuccess, socket } = useAuth();
    const { updateJobProgress, connectEnterpriseSSE, disconnectEnterpriseSSE, isEnterpriseConnected } = useJobProgress();
    const {
        activeSubscription,
        usageInfo,
        checkFeatureAccess,
        incrementVodCount, // Assuming this is still needed, otherwise remove
        getFfmpegSettings,
        getEditorSettings,
        isLoading: isSubscriptionLoading, // Get isLoading state from SubscriptionContext
        fetchUsageStats // fetchUsageStats fonksiyonunu context'ten al
    } = useSubscription();

    // currentUser local state and its syncing useEffect have been removed.
    // We will rely directly on contextUser and isAuthenticated from AuthContext.

    // The second call to useSubscription() and its destructuring has been removed
    // as it was redundant and caused redeclaration errors.
    // All necessary values are obtained from the first call to useSubscription() above.

    const [jobs, setJobs] = useState<Job[]>([]);
    const [loading, setLoading] = useState(true); // This is for jobs loading
    const [error, setError] = useState<string | null>(null);
    const [subscriptionAlert, setSubscriptionAlert] = useState<{ type: 'info' | 'warning' | 'error', message: string } | null>(null);
    const [subscriptionDataStable, setSubscriptionDataStable] = useState(false);

    // User clips logic is now in UserClipsGallery component
    // const [userClips, setUserClips] = useState<Clip[]>([]);
    // const [loadingClips, setLoadingClips] = useState(true);
    // const [errorClips, setErrorClips] = useState<string | null>(null);
    // const clipsByJob = React.useMemo(() => { ... }); // This logic is moved

    const navigate = useNavigate();
    const [selectedWhisperModel, setSelectedWhisperModel] = useState('base'); // Bu state VodUploadSection'da artık kullanılmıyor ama burada kalabilir.
    const [selectedWhisperDevice, setSelectedWhisperDevice] = useState<'cpu' | 'cuda'>('cuda'); // Varsayılan cihaz GPU (cuda) ve tipi belirttik
    const [customLlmPrompt, setCustomLlmPrompt] = useState('');
    const [clipCount, setClipCount] = useState(5);
    const [ffmpegSettings, setFfmpegSettings] = useState({ resolution: '1280x720', fps: 30 });
    const [editorSettings, setEditorSettings] = useState({ resolution: '1280x720', fps: 30 });
    const [customPromptAllowed, setCustomPromptAllowed] = useState(false);
    const [useModernUI] = useState(true); // Modern UI always on

    useEffect(() => {
        let timerId: NodeJS.Timeout;
        if (!authLoading && !isSubscriptionLoading) {
            // Both auth and subscription contexts have finished their loading phases.
            // Add a small delay to prevent flickering during rapid state changes
            timerId = setTimeout(() => {
                setSubscriptionDataStable(true);
            }, 100); // Increased delay to prevent flickering
        } else {
            // Only set to false if we're currently stable to avoid unnecessary changes
            setSubscriptionDataStable(prev => prev ? false : prev);
        }
        return () => clearTimeout(timerId); // Cleanup the timeout if the component unmounts or dependencies change
    }, [authLoading, isSubscriptionLoading]);

    // --- Effects for loading settings and jobs ---
    useEffect(() => {
        const loadSettingsAndAlerts = async () => {
            if (!subscriptionDataStable) {
                setSubscriptionAlert(null); // Clear alerts while not stable
                return;
            }
            
            try {
                const ffSettings = await getFfmpegSettings();
                setFfmpegSettings(ffSettings);
                const edSettings = await getEditorSettings();
                setEditorSettings(edSettings);
                const promptAccess = await checkFeatureAccess('custom-ai-prompt');
                setCustomPromptAllowed(promptAccess.allowed);

                if (!activeSubscription) {
                    setSubscriptionAlert({ type: 'warning', message: 'You do not have an active subscription. Please select a subscription plan to upload VODs and create clips.' });
                } else if (usageInfo && activeSubscription.tier && usageInfo.remaining?.dailyVods === 0 && usageInfo.limits?.dailyVodUploadLimit < 999999) {
                    setSubscriptionAlert({ type: 'error', message: `You have reached your daily VOD upload limit (${usageInfo.currentUsage?.dailyVodsUploaded ?? 0}/${usageInfo.limits?.dailyVodUploadLimit ?? 'N/A'}). Upgrade your subscription plan or wait for the next period to upload more VODs.` });
                } else if (usageInfo && activeSubscription.tier && activeSubscription.tier.maxClipsPerVod > 0 && activeSubscription.tier.maxClipsPerVod < 999999 && usageInfo.remaining?.dailyVods === 0 ) {
                    setSubscriptionAlert({ type: 'warning', message: `You cannot create more clips as you've reached your VOD upload limit. Max clips per VOD is ${activeSubscription.tier.maxClipsPerVod}.` });
                } else if (usageInfo && activeSubscription.tier && (usageInfo.remaining?.dailyVods !== undefined && usageInfo.remaining.dailyVods !== -1 && usageInfo.remaining.dailyVods <= 2 )) {
                    setSubscriptionAlert({ type: 'warning', message: `Remaining daily VODs: ${usageInfo.remaining?.dailyVods ?? 'N/A'}. Your limit is about to expire.` });
                } else {
                    setSubscriptionAlert(null);
                }
            } catch (err) {
                setSubscriptionAlert({ type: 'error', message: 'Failed to load subscription settings.' });
            }
        };
        
        loadSettingsAndAlerts();
    }, [activeSubscription, usageInfo, checkFeatureAccess, getEditorSettings, getFfmpegSettings, subscriptionDataStable]); // Depends on subscriptionDataStable now

    const fetchJobs = useCallback(async (showLoadingState = false) => {
        if (!isAuthenticated || !contextUser?.id) {
            setJobs([]); // Clear jobs if user is not authenticated
            setLoading(false);
            return;
        }
        try {
            // Only set loading if explicitly requested (initial load)
            if (showLoadingState) {
                setLoading(true);
            }
            const response = await api.get('/jobs');
            const validJobs: Job[] = response.data.filter((job: Job | null | undefined) => job !== null && job !== undefined);
            
            // Debug: Log fetched jobs to see what we have
            console.log(`[Dashboard DEBUG] Fetched ${validJobs.length} jobs:`, validJobs.map(job => ({
                id: job.id,
                status: job.status,
                vodTitle: job.vodTitle,
                platform: job.platform,
                createdAt: job.createdAt
            })));
            
            setJobs(validJobs);
            setError(null);
            
            // 🚀 Start Enterprise SSE for active jobs
            const activeJobIds = validJobs
                .filter(job => job.status !== 'COMPLETED' && job.status !== 'FAILED' && job.status !== 'CANCELLED')
                .map(job => job.id);
            
            if (activeJobIds.length > 0 && contextUser?.id) {
                console.log(`🚀 [Dashboard] Starting Enterprise SSE for ${activeJobIds.length} active jobs:`, activeJobIds);
                connectEnterpriseSSE(activeJobIds, contextUser.id);
            } else if (activeJobIds.length === 0) {
                console.log('🛑 [Dashboard] No active jobs, disconnecting Enterprise SSE');
                disconnectEnterpriseSSE();
            }
        } catch (err: any) {
            setError(err.response?.data?.message || err.message || 'An error occurred while fetching jobs.');
        } finally {
            if (showLoadingState) {
                setLoading(false);
            }
        }
    }, [setJobs, setError, setLoading, contextUser, isAuthenticated, authLoading, connectEnterpriseSSE, disconnectEnterpriseSSE]); // Added connectEnterpriseSSE, disconnectEnterpriseSSE

    const pollingStartedRef = React.useRef(false);
    const intervalIdRef = React.useRef<NodeJS.Timeout | null>(null);

    const startPollingAndSetInterval = useCallback(() => {
        if (pollingStartedRef.current) {
            // Polling already started, do nothing.
            return;
        }
        fetchJobs(true); // Initial fetch with loading state
        // The setInterval logic is removed to prevent conflicts with WebSocket updates.
        // All subsequent updates will be handled by the SocketContext and JobProgressContext.
        pollingStartedRef.current = true;
    }, [fetchJobs]);

    const stopPolling = useCallback(() => {
        // Since setInterval is removed, this function now only resets the ref flag.
        pollingStartedRef.current = false;
    }, []);

    const handleLoginSuccess = useCallback((loggedInUser: User) => {
        // This loggedInUser comes directly from AuthContext's login function after successful authentication and setUser.
        if (loggedInUser?.id) {
            setJobs([]);
            pollingStartedRef.current = false;
            // It's crucial that startPollingAndSetInterval -> fetchJobs uses a reliable way to identify the user for the API call.
            // Since fetchJobs uses the global `api` instance which should have the token set by AuthContext,
            // it should work even if the `contextUser` in `Dashboard`'s own scope hasn't updated yet for this very first render cycle.
            startPollingAndSetInterval();
        } else {
            stopPolling();
            setJobs([]);
            setLoading(false);
        }
    }, [startPollingAndSetInterval, setJobs, stopPolling, setLoading]);

    // Effect for registering and unregistering the login success callback
    useEffect(() => {
        registerOnLoginSuccess(handleLoginSuccess);
        return () => {
            unregisterOnLoginSuccess(handleLoginSuccess);
            stopPolling(); // Also stop polling when the component unmounts
        };
    }, [registerOnLoginSuccess, unregisterOnLoginSuccess, handleLoginSuccess, stopPolling]);

    // Effect for handling initial load (F5) and changes to contextUser or authLoading
    useEffect(() => {
        if (!authLoading && isAuthenticated && contextUser?.id) {
            // This block handles F5 refresh or when auth state is resolved after initial load
            if (!pollingStartedRef.current) {
                setJobs([]); // Clear any stale jobs from a previous session or state
                pollingStartedRef.current = false; // Explicitly reset polling flag
                startPollingAndSetInterval();
            } else {
                // Optionally, could fetch jobs once if concerned about staleness without full polling restart
                // fetchJobs();
            }
        } else if (!authLoading && (!isAuthenticated || !contextUser?.id)) {
            // This block handles logout or if auth check resolves to no user
            stopPolling();
            setJobs([]);
            setLoading(false); // Ensure loading is false if there's no user to fetch jobs for
        }
        // If authLoading is true, we do nothing and wait for it to become false.
        // The main return already handles showing a loading spinner for authLoading.
    }, [isAuthenticated, contextUser, authLoading, startPollingAndSetInterval, stopPolling, setJobs, setLoading]); // Dependencies updated

    // Socket listener for real-time job progress updates
    useEffect(() => {
        if (socket && isAuthenticated) {
            const handleJobUpdate = (data: any) => {
                // Only log status changes, not every progress update
                if (import.meta.env.DEV && data.status && !data.downloadProgress && !data.subtitleProgress) {
                    console.log('[Dashboard] Job status update:', data.jobId, data.status);
                }
                
                // Update job progress context
                updateJobProgress(data.jobId || data.id, {
                    jobId: data.jobId || data.id,
                    status: data.status,
                    progress: data.progress,
                    displayText: data.detail || data.displayText,
                    currentStageDetail: data.detail,
                    timestamp: data.timestamp || Date.now(),
                    elapsedTime: data.elapsedTime,
                    downloadProgress: data.downloadProgress,
                    subtitleProgress: data.subtitleProgress,
                    transcriptionRate: data.transcriptionRate,
                    processedFragments: data.processedFragments,
                    totalFragments: data.totalFragments,
                    downloadedSize: data.downloadedSize,
                    downloadRate: data.downloadRate,
                    isIndeterminate: data.isIndeterminate,
                    isDownloadActuallyCompleted: data.isDownloadActuallyCompleted,
                    clipsProcessedCount: data.clipsProcessedCount,
                    clipsTotalCount: data.clipsTotalCount
                });
                
                // Also update the jobs list if status has changed
                if (data.status) {
                    setJobs(prevJobs => 
                        prevJobs.map(job => 
                            job.id === (data.jobId || data.id) 
                                ? { ...job, status: data.status }
                                : job
                        )
                    );
                }
            };

            socket.on('job_update', handleJobUpdate);

            return () => {
                socket.off('job_update', handleJobUpdate);
            };
        }
        return undefined;
    }, [socket, isAuthenticated, updateJobProgress, setJobs]);

    // Kullanıcı giriş yaptığında veya dashboard ilk yüklendiğinde kullanım istatistiklerini çek
    useEffect(() => {
        if (isAuthenticated && subscriptionDataStable) {
            fetchUsageStats();
        }
    }, [isAuthenticated, subscriptionDataStable, fetchUsageStats]);

    // fetchUserClips logic is now in UserClipsGallery component
    // useEffect(() => { ... });

    const handleJobRetried = (jobId: string) => {
        fetchJobs(false); // Re-fetch jobs to update the list without loading state
        // Optionally, you can add a small delay or a more sophisticated update mechanism
        // to ensure the backend has processed the retry and updated the status.
        // For now, an immediate fetch is a good starting point.
    };

    // --- Handlers (logic remains the same) ---
    const handleCancelJob = async (jobId: string) => {
        // 🚀 Optimistic Update - Update UI immediately
        setJobs(prevJobs => 
            prevJobs.map(job => 
                job.id === jobId 
                    ? { ...job, status: 'CANCELLED' as any }
                    : job
            )
        );

        try {
            const response = await api.put(`/jobs/${jobId}/cancel`);
            
            // If backend says job was already cancelled, that's fine
            if (response.data?.message?.includes('already cancelled')) {
                console.log(`[handleCancelJob] Job ${jobId} was already cancelled`);
            }
            
            // Re-fetch to ensure consistency without loading state
            fetchJobs(false);
        } catch (error: any) {
            // Revert optimistic update on error
            fetchJobs(false);
            
            // Re-throw the error so the JobItem can handle it
            throw error;
        }
    };

    const handleDeleteJob = async (jobId: string) => {
        try {
            // Optimistic update: Remove the job from the list immediately
            // This provides a faster perceived response to the user.
            setJobs(prevJobs => prevJobs.filter(job => job.id !== jobId));
            
            // Send delete request to the backend
            await api.delete(`/jobs/${jobId}`);
            
            // After backend confirms (or if it partially deleted),
            // re-fetch the entire list to ensure consistency with the backend state.
            // If the job was only partially deleted by the backend (due to edited clips),
            // it will reappear in the list after this fetch.
            // If it was fully deleted, it will remain gone.
            // This handles both cases without needing a specific response from the backend
            // about whether it was a full or partial delete.
            fetchJobs(false);
            // Optionally, show a success toast message here.
        } catch (error: any) {
            console.error(`Error deleting job ${jobId}:`, error);
            // If an error occurs (e.g., network issue, server error),
            // it's good to re-fetch the jobs list to revert the optimistic update
            // or show an error message to the user.
            // For simplicity, we'll re-fetch to ensure UI consistency.
            try {
                fetchJobs(false);
            } catch (fetchError) {
                console.error(`Error re-fetching jobs after delete error for job ${jobId}:`, fetchError);
                // Handle this error appropriately, e.g., show a generic error message.
            }
            // Optionally, show an error toast message here.
        }
    };

     const handleBeforeVodUpload = async () => {
        try {
            const vodAccess = await checkFeatureAccess('vod-upload');
            if (!vodAccess.allowed) {
                setError(vodAccess.message || 'You do not have permission to upload VODs.');
                return false;
            }
            return true;
        } catch (err) {
            setError('An error occurred while checking VOD upload permission.');
            return false;
        }
    };

    const handleAfterVodUpload = async () => {
        // console.log('[Dashboard] handleAfterVodUpload called'); // DEBUG
        try {
            await incrementVodCount();
            // After a VOD is successfully uploaded and a job is created,
            // immediately fetch the jobs list to show the new job in the queue.
            // Don't show loading state for this update
            fetchJobs(false);
        } catch (err) {
            console.error('[Dashboard] VOD count increment or job fetch error in handleAfterVodUpload:', err); // DEBUG (added context prefix)
        }
    };

    // --- Modern Glassmorphism Subscription Card ---
    const SubscriptionCard = () => {
        // Show skeleton loading state during initial subscription data fetch
        // This prevents flickering from null -> "no subscription" -> actual data
        if (isSubscriptionLoading) {
            return (
                <div className="relative group mb-6">
                    {/* Background gradient glow */}
                    <div className="absolute -inset-0.5 bg-gradient-to-r from-gray-500/20 via-slate-500/20 to-gray-500/20 rounded-2xl opacity-30 blur-xl" />
                    
                    <div className="
                        relative bg-black/40 backdrop-blur-xl border border-white/10 
                        rounded-2xl overflow-hidden shadow-2xl
                    ">
                        <div className="p-6 md:p-8">
                            <div className="flex items-center gap-3 mb-4">
                                <div className="p-2 rounded-xl bg-gradient-to-r from-gray-500/20 to-slate-500/20 border border-white/10">
                                    <div className="w-5 h-5 bg-gray-400/50 rounded animate-pulse" />
                                </div>
                                <div className="h-6 bg-gray-400/20 rounded w-48 animate-pulse"></div>
                            </div>
                            
                            <div className="space-y-3">
                                <div className="h-4 bg-gray-400/20 rounded w-3/4 animate-pulse"></div>
                                <div className="h-4 bg-gray-400/20 rounded w-1/2 animate-pulse"></div>
                            </div>
                            
                            <div className="mt-6">
                                <div className="h-10 bg-gray-400/20 rounded-xl w-48 animate-pulse"></div>
                            </div>
                        </div>
                    </div>
                </div>
            );
        }

        if (!activeSubscription) {
            return (
                <div className="relative group mb-6">
                    {/* Background gradient glow */}
                    <div className="absolute -inset-0.5 bg-gradient-to-r from-purple-500/20 via-blue-500/20 to-cyan-500/20 rounded-2xl opacity-30 blur-xl group-hover:opacity-50 transition-opacity duration-300" />
                    
                    <div className="
                        relative bg-black/40 backdrop-blur-xl border border-white/10 
                        rounded-2xl overflow-hidden shadow-2xl
                        hover:bg-black/50 hover:border-white/20 transition-all duration-300
                    ">
                        <div className="p-6 md:p-8">
                            <div className="flex items-center gap-3 mb-4">
                                <div className="p-2 rounded-xl bg-gradient-to-r from-purple-500/20 to-blue-500/20 border border-white/10">
                                    <Crown size={20} className="text-purple-400" />
                                </div>
                                <h2 className="text-xl font-semibold text-white">Subscription Status</h2>
                            </div>
                            
                            <p className="text-white/70 mb-6 leading-relaxed">
                                You do not have an active subscription. Please select a subscription plan to upload VODs and create clips.
                            </p>
                            
                            <Link
                                to="/subscription"
                                className="
                                    group/btn inline-flex items-center gap-2 px-6 py-3 rounded-xl
                                    bg-gradient-to-r from-purple-500 to-blue-500 text-white font-medium
                                    hover:from-purple-600 hover:to-blue-600 shadow-lg hover:shadow-xl
                                    transition-all duration-200 transform hover:scale-105
                                "
                            >
                                <Crown size={16} />
                                View Subscription Plans
                                <ArrowRight size={16} className="group-hover/btn:translate-x-0.5 transition-transform" />
                            </Link>
                        </div>
                    </div>
                </div>
            );
        }

        // Helper functions from SubscriptionPage's UsageStats
        const calculateRemainingPercentage = (rem: number, limit: number): number => {
            if (limit <= 0 || limit >= 999999 || limit === -1) return 100;
            return Math.min(100, Math.max(0, (rem / limit) * 100));
        };

        const formatDisplayText = (rem: number, limit: number): string => {
            if (limit >= 999999 || limit === -1) return '∞';
            if (limit <= 0) return 'N/A';
            return `${rem}`;
        };

        const dailyVodRemainingPercentage = usageInfo ? calculateRemainingPercentage(usageInfo.remaining.dailyVods, usageInfo.limits.dailyVodUploadLimit) : 0;
        const weeklyVodRemainingPercentage = usageInfo ? calculateRemainingPercentage(usageInfo.remaining.weeklyVods, usageInfo.limits.weeklyVodUploadLimit) : 0;
        const monthlyVodRemainingPercentage = usageInfo ? calculateRemainingPercentage(usageInfo.remaining.monthlyVods, usageInfo.limits.monthlyVodUploadLimit) : 0;
        const redetectionRemainingPercentage = usageInfo ? calculateRemainingPercentage(usageInfo.remaining.monthlyRedetections, usageInfo.limits.monthlyRedetectionLimit) : 0;

        const usageItems = usageInfo ? [
            {
                label: 'Daily VODs',
                percentage: dailyVodRemainingPercentage,
                color: 'text-sky-500',
                remaining: usageInfo.remaining.dailyVods,
                limit: usageInfo.limits.dailyVodUploadLimit,
                displayText: formatDisplayText(usageInfo.remaining.dailyVods, usageInfo.limits.dailyVodUploadLimit)
            },
            {
                label: 'Weekly VODs',
                percentage: weeklyVodRemainingPercentage,
                color: 'text-lime-500',
                remaining: usageInfo.remaining.weeklyVods,
                limit: usageInfo.limits.weeklyVodUploadLimit,
                displayText: formatDisplayText(usageInfo.remaining.weeklyVods, usageInfo.limits.weeklyVodUploadLimit)
            },
            {
                label: 'Monthly VODs',
                percentage: monthlyVodRemainingPercentage,
                color: 'text-amber-500',
                remaining: usageInfo.remaining.monthlyVods,
                limit: usageInfo.limits.monthlyVodUploadLimit,
                displayText: formatDisplayText(usageInfo.remaining.monthlyVods, usageInfo.limits.monthlyVodUploadLimit)
            },
            {
                label: 'Redetections',
                percentage: redetectionRemainingPercentage,
                color: 'text-violet-500',
                remaining: usageInfo.remaining.monthlyRedetections,
                limit: usageInfo.limits.monthlyRedetectionLimit,
                displayText: formatDisplayText(usageInfo.remaining.monthlyRedetections, usageInfo.limits.monthlyRedetectionLimit)
            },
        ] : [];

        // Get tier color for gradient
        const getTierGradient = (tierName: string) => {
            switch (tierName) {
                case 'Tier 2':
                    return 'from-blue-500/20 via-cyan-500/20 to-blue-500/20';
                case 'Tier 3':
                    return 'from-purple-500/20 via-pink-500/20 to-purple-500/20';
                default:
                    return 'from-gray-500/20 via-slate-500/20 to-gray-500/20';
            }
        };

        const getTierColor = (tierName: string) => {
            switch (tierName) {
                case 'Tier 2':
                    return 'text-blue-400';
                case 'Tier 3':
                    return 'text-purple-400';
                default:
                    return 'text-gray-400';
            }
        };

        return (
            <div className="relative group mb-6">
                {/* Background gradient glow */}
                <div className={`
                    absolute -inset-0.5 bg-gradient-to-r ${getTierGradient(activeSubscription?.tier.name || '')} 
                    rounded-2xl opacity-30 blur-xl group-hover:opacity-50 transition-opacity duration-300
                `} />
                
                <div className="
                    relative bg-black/40 backdrop-blur-xl border border-white/10 
                    rounded-2xl overflow-hidden shadow-2xl
                    hover:bg-black/50 hover:border-white/20 transition-all duration-300
                ">
                    <div className="p-6 md:p-8">
                        {/* Header Section */}
                        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center mb-6">
                            {/* Left: Title and Plan Info */}
                            <div className="mb-4 lg:mb-0">
                                <div className="flex items-center gap-3 mb-2">
                                    <div className={`
                                        p-2 rounded-xl bg-gradient-to-r ${getTierGradient(activeSubscription?.tier.name || '')} 
                                        border border-white/10
                                    `}>
                                        <Crown size={20} className={getTierColor(activeSubscription?.tier.name || '')} />
                                    </div>
                                    <h2 className="text-xl font-semibold text-white">Subscription Status</h2>
                                </div>
                                <div className="flex items-center gap-3">
                                    <p className="text-white/70">
                                        Current Plan: <span className="font-medium text-white">{activeSubscription?.tier.name}</span>
                                    </p>
                                    <div className={`
                                        inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium
                                        bg-gradient-to-r ${activeSubscription?.tier.name === 'Tier 2' ? 'from-blue-500 to-cyan-500' : 
                                            activeSubscription?.tier.name === 'Tier 3' ? 'from-purple-500 to-pink-500' : 'from-gray-500 to-gray-600'} 
                                        text-white shadow-lg
                                    `}>
                                        <Crown size={10} />
                                        {activeSubscription?.tier.name}
                                    </div>
                                </div>
                            </div>

                            {/* Right: Renewal Date and Action */}
                            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                                <div className="text-right">
                                    <p className="text-xs text-white/50 uppercase tracking-wide font-medium">Renews On</p>
                                    <p className="text-sm text-white font-medium">
                                        {activeSubscription?.endDate ? 
                                            new Date(activeSubscription.endDate).toLocaleDateString('en-US', { 
                                                month: 'short', 
                                                day: 'numeric', 
                                                year: 'numeric' 
                                            }) : 'N/A'
                                        }
                                    </p>
                                </div>
                                <Link
                                    to="/subscription"
                                    className="
                                        group/btn inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium
                                        bg-white/10 border border-white/20 text-white
                                        hover:bg-white/15 hover:border-white/30 transition-all duration-200
                                    "
                                >
                                    Manage Plan
                                    <ArrowRight size={14} className="group-hover/btn:translate-x-0.5 transition-transform" />
                                </Link>
                            </div>
                        </div>

                        {/* Usage Statistics */}
                        {usageInfo && (
                            <div className="space-y-4">
                                {/* Usage Items Grid */}
                                <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-5 gap-4">
                                    {usageItems.map((item) => (
                                        (item.limit > 0 || item.limit >= 999999 || item.limit === -1) && (
                                            <div key={item.label} className="
                                                flex flex-col items-center p-4 rounded-xl
                                                bg-white/5 border border-white/10 hover:bg-white/10 
                                                transition-all duration-200 group/item
                                            ">
                                                <p className="text-xs font-medium text-white/60 mb-3 text-center">
                                                    {item.label}
                                                </p>
                                                <CircularProgress
                                                    percentage={item.percentage}
                                                    color={item.color}
                                                    displayText={item.displayText}
                                                    size={60}
                                                    strokeWidth={5}
                                                />
                                                {item.limit < 999999 && item.limit > 0 && item.limit !== -1 && (
                                                    <p className="text-xs text-white/50 mt-3 text-center">
                                                        {item.remaining} / {item.limit}
                                                    </p>
                                                )}
                                                {item.limit === -1 && (
                                                    <p className="text-xs text-white/50 mt-3 text-center">
                                                        Unlimited
                                                    </p>
                                                )}
                                            </div>
                                        )
                                    ))}
                                    
                                    {/* Max Clips per VOD */}
                                    <div className="
                                        flex flex-col items-center p-4 rounded-xl
                                        bg-white/5 border border-white/10 hover:bg-white/10 
                                        transition-all duration-200
                                    ">
                                        <p className="text-xs font-medium text-white/60 mb-3 text-center">
                                            Max Clips/VOD
                                        </p>
                                        <div className="flex items-center justify-center h-[60px]">
                                            <p className="text-2xl font-bold text-white">
                                                {usageInfo.limits.maxClipsPerVod >= 999999 || usageInfo.limits.maxClipsPerVod === -1 ? '∞' : usageInfo.limits.maxClipsPerVod}
                                            </p>
                                        </div>
                                        <p className="text-xs text-white/50 mt-3 opacity-0">
                                            0 / 0
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    // --- Tailwind Refactored Main Return ---
    // Show a loading spinner only if auth is loading or if both subscription is loading AND we don't have initial data
    // This prevents flickering when subscription data is being refreshed
    const shouldShowLoading = authLoading || (isSubscriptionLoading && !subscriptionDataStable);
    
    if (shouldShowLoading) {
        // Use consistent loading message to prevent flickering between different states
        const loadingMessage = "Loading your dashboard...";
        
        return (
            <div className="min-h-screen flex flex-col items-center justify-center text-foreground bg-background">
                <svg className="animate-spin h-12 w-12 text-primary mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <p className="text-xl text-foreground">{loadingMessage}</p>
            </div>
        );
    }

    // All data is loaded and stable, render the main dashboard content.
    return (
        <div className="min-h-screen py-8 bg-background" style={{
          backgroundImage: `
            linear-gradient(to right, rgba(255, 255, 255, 0.05) 1px, transparent 1px),
            linear-gradient(to bottom, rgba(255, 255, 255, 0.05) 1px, transparent 1px)
          `,
          backgroundSize: '2rem 2rem',
        }}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-foreground"> {/* Container */}
            {/* Header */}
            <div className="mb-6">
              <h1 className="text-3xl font-bold text-foreground">Dashboard</h1>
            </div>

            {/* subscriptionAlert is now set by an effect that waits for subscriptionDataStable */}
            {subscriptionAlert && (
                <div className={`px-4 py-3 rounded relative mb-6 ${getAlertStyles(subscriptionAlert.type)}`} role="alert">
                    {subscriptionAlert.message}
                </div>
            )}
            {/* General error for job fetching and operations */}
            {error && (
                 <div className={`px-4 py-3 rounded relative mb-6 ${getAlertStyles('error')}`} role="alert">
                    {error}
                </div>
            )}

            {/* SubscriptionCard is rendered here; activeSubscription should be stable */}
            <SubscriptionCard />

            {/* Grid Layout */}
            <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
                {/* Video Upload Section (Left Column) */}
                 <div className="md:col-span-5">
                    {useModernUI ? (
                        <ModernVodUploadForm
                            initialSelectedDevice={selectedWhisperDevice === 'cuda' ? 'GPU' : 'CPU'}
                            onDeviceChange={(device) => setSelectedWhisperDevice(device === 'GPU' ? 'cuda' : 'cpu')}
                            customLlmPrompt={customLlmPrompt}
                            setCustomLlmPrompt={setCustomLlmPrompt}
                            clipCount={clipCount}
                            setClipCount={setClipCount}
                            customPromptAllowed={customPromptAllowed}
                            onBeforeUpload={handleBeforeVodUpload}
                            onAfterUpload={handleAfterVodUpload}
                        />
                    ) : (
                        <VodUploadSection
                            selectedWhisperModel={selectedWhisperModel}
                            selectedWhisperDevice={selectedWhisperDevice}
                            setSelectedWhisperModel={setSelectedWhisperModel}
                            setSelectedWhisperDevice={setSelectedWhisperDevice as (value: 'cpu' | 'cuda') => void}
                            customLlmPrompt={customLlmPrompt}
                            setCustomLlmPrompt={setCustomLlmPrompt}
                            clipCount={clipCount}
                            setClipCount={setClipCount}
                            customPromptAllowed={customPromptAllowed}
                            onBeforeUpload={handleBeforeVodUpload}
                            onAfterUpload={handleAfterVodUpload}
                        />
                    )}
                </div>

                {/* Job Queue List (Right Column) */}
                <div className="md:col-span-7">
                    <JobQueueList
                        jobs={jobs as any} // Type assertion to resolve interface differences
                        loading={loading} // This loading is for fetching jobs
                        error={error} // Pass error state if JobQueueList needs it
                        onCancelJob={handleCancelJob}
                        onDeleteJob={handleDeleteJob}
                        onJobRetried={handleJobRetried} // Pass the new handler
                        useModernUI={useModernUI}
                    />
                </div>
            </div>
          </div>
        </div>
    );
}

export default Dashboard;